import React from 'react';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { SectionData } from '@/types';
import { useThemeColors } from '@/hooks/useThemeColors';

interface LockedSectionCardProps {
    section: SectionData;
    sectionNumber?: number;
}

const LockedSectionCard: React.FC<LockedSectionCardProps> = ({
    section,
    sectionNumber,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    const getConditionStatusIcon = (isMet: boolean) => {
        return isMet ? (
            <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
            <AlertCircle className="h-4 w-4 text-orange-500" />
        );
    };

    const getConditionStatusText = (isMet: boolean) => {
        return isMet ? t('section.condition.completed') : t('section.condition.pending');
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden opacity-75">
            {/* Section Header */}
            <div className="p-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-300 rounded-full">
                            <span className="text-sm font-medium text-gray-600">
                                {sectionNumber}
                            </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-600">
                            {section.title}
                        </h3>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Lock className="h-5 w-5 text-gray-500" />
                        <span className="text-sm font-medium text-gray-500">
                            {t('section.status')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Unlock Requirements */}
            <div className="p-4">
                <div className="mb-3">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                        {t('section.unlockRequirements')}
                    </h4>
                </div>

                {section.unlock_conditions.length === 0 ? (
                    <div className="flex items-center space-x-2 text-gray-600">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm">
                            {t('section.manualUnlockRequired')}
                        </span>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {section.unlock_conditions.map((condition, index) => (
                            <div
                                key={index}
                                className={clsx(
                                    'flex items-center space-x-2 p-2 rounded-lg',
                                    condition.is_met
                                        ? 'bg-green-50 border border-green-200'
                                        : 'bg-orange-50 border border-orange-200'
                                )}
                            >
                                {getConditionStatusIcon(condition.is_met)}
                                <div className="flex-1">
                                    <span
                                        className={clsx(
                                            'text-sm',
                                            condition.is_met
                                                ? 'text-green-800'
                                                : 'text-orange-800'
                                        )}
                                    >
                                        {condition.description}
                                    </span>
                                    <span
                                        className={clsx(
                                            'ml-2 text-xs font-medium',
                                            condition.is_met
                                                ? 'text-green-600'
                                                : 'text-orange-600'
                                        )}
                                    >
                                        ({getConditionStatusText(condition.is_met)})
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Section Stats */}
                <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>
                            {section.topics.length} {t('section.topics', { count: section.topics.length })}
                        </span>
                        <span>
                            {section.evaluations.length} {t('section.evaluations', { count: section.evaluations.length })}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LockedSectionCard;